import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT } from '@us/app-config';
import { Store } from '@ngrx/store';
import { selectPersistedUserId, selectValidatedToken } from '@us/web/auth/data-access';
import Pusher from 'pusher-js/with-encryption';
import { filter, map, Subject } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { tuiIsPresent } from '@taiga-ui/cdk';
import { PusherEvent } from '../enums/pusher-event.enum';
import { BroadcastNotificationCreated } from '../models/broadcast-notification-created';
import { Order } from '@us/web/shared/data-access';
import { OrderUpdated } from '../models/order-updated';
import { parseDataServer } from '@us/web/shared/utils';

@Injectable({
    providedIn: 'root'
})
export class PusherService {
    private readonly env = inject(ENVIRONMENT);
    private readonly store = inject(Store);

    private readonly authToken$ = this.store.select(selectValidatedToken);
    private readonly sessionUserId = this.store.selectSignal(selectPersistedUserId);

    readonly socketId = signal<string | null>(null);

    readonly pusherClient = toSignal(
        this.authToken$.pipe(
            filter(tuiIsPresent),
            map(
                ({ accessToken }) =>
                    new Pusher(this.env.pusher.appKey, {
                        wsHost: this.env.pusher.wsHost,
                        wsPort: this.env.pusher.wsPort,
                        wssPort: this.env.pusher.wssPort,
                        cluster: this.env.pusher.cluster,
                        forceTLS: false, // TODO: change to true
                        disableStats: true,
                        enabledTransports: ['wss', 'ws'],
                        userAuthentication: {
                            endpoint: this.env.apiURL + '/broadcasting/auth',
                            transport: 'ajax',
                            params: {},
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        },
                        channelAuthorization: {
                            endpoint: this.env.apiURL + '/broadcasting/auth',
                            transport: 'ajax',
                            params: {},
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }
                    })
            )
        )
    );

    readonly userChannel = computed(() => {
        const sessionUserId = this.sessionUserId();
        const pusherClient = this.pusherClient();
        if (!sessionUserId || !pusherClient) {
            return;
        }

        return pusherClient.subscribe(`private-users.${sessionUserId}`);
    });

    readonly orderUpdated$ = new Subject<OrderUpdated>();

    constructor() {
        effect(() => {
            const pusherClient = this.pusherClient();
            if (!pusherClient) {
                return;
            }

            pusherClient.connection.bind('connected', () => {
                this.socketId.set(pusherClient.connection.socket_id);

                console.log('💬 Pusher Connected');
            });

            pusherClient.connection.bind('disconnected', () => {
                console.log('💬 Pusher Disconnected');
            });

            pusherClient.connection.bind('error', (err: unknown) => {
                console.log('💬 Pusher Error', err);
            });

            pusherClient.bind_global(function (event: unknown, data: unknown) {
                console.log('💬 Pusher Global', event, data);
            });
        });

        effect(() => {
            const channel = this.userChannel();
            if (!channel) {
                return;
            }

            channel.bind('pusher:subscription_succeeded', () => {
                console.log(`${channel.name} subscription_succeeded`);
            });

            channel.bind('pusher:subscription_error', (error: unknown) => {
                console.error(`${channel.name} pusher:subscription_error`, error);
            });

            channel.bind(
                PusherEvent.BROADCAST_NOTIFICATION_CREATED,
                ({ id, type, ...items }: BroadcastNotificationCreated) => {
                    switch (type) {
                        case 'order.updated':
                            for (const idx in items) {
                                const value = items[idx] as OrderUpdated;
                                value.order = parseDataServer(value.order);
                                this.orderUpdated$.next(value);
                            }
                            break;
                    }
                }
            );
        });
    }
}
